import React, { useState, useContext, useMemo, useEffect } from 'react'
import { ThemeProvider } from "@pixel/core";
import { createInternalTheme } from "@pixel/internal/styles";

import Typography from '@pixel/core/Typography';
import Paper from "@pixel/core/Paper";
import Chip from "@pixel/core/Chip";
import DataTable from '@pixel/core/DataTable'
import Link from '@pixel/core/Link'
import Box from '@pixel/core/Box';
import ListBase, {
    ListSearchChangeProps,
    ListAppliedSearchFilterProps,
} from '@pixel/core/ListBase';

import PageHeader from "@pixel/core/PageHeader";
import LayoutRoot from "@pixel/internal/LayoutRoot";
import LayoutRootSection from "@pixel/internal/LayoutRootSection";
import CircularProgress from '@pixel/core/CircularProgress';
import TableChartIcon from '@pixel/assets/icons/TableChart';
import Tooltip from '@pixel/core/Tooltip';
import { SelectChangeEvent } from '@mui/material/Select';
import Moment from "moment";

import Breadcrumb from "@pixel/core/Breadcrumb";
import BreadcrumbItem from "@pixel/core/BreadcrumbItem";

import ActionGroup from '@pixel/core/ActionGroup';
import OdsContext from '../OdsContext';
import { useMultipleLocalizedText } from "../../../contexts/Localization/localizationHooks";
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { transformLocationsResult, isAvailableInBreadcrumb, updateBreadcrumb, transformLocationFilterData } from '../OdsTransform';
import { ILocationsData, ILocationsGetParms, ILocationsResultData, IBreadcrumbHierarchyObject, ILocationsSearchGetParms, ILocationsFilterGetParms } from "../OdsTypes";
import Button from '@pixel/core/Button';
import axios, { AxiosResponse } from "axios";
import AddLocation from './AddLocation';
import { useSnackbar } from '@pixel/core/SnackbarService';
import { createdByFitler, statusFilter } from './SearchFiltersHelper';

const Locations = () => {

    const overlayStyling = {
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        alignItems: 'center',
        zIndex: '1',
    }

    const {
        ODS_TAB_LOCATIONS,
        ODS_ALL,
        ODS_SEARCH_PLACEHOLDER,
        ODS_SEARCH_LOCATIONS,
        ODS_CREATED_BY,
        ODS_DATE_CREATED,
        ODS_SUB_LOCATIONS,
        ODS_STATUS,
        I18N_NO_RESULTS_FOUND,
        ODS_ACTIONS_DUPLICATE,
        ODS_ACTIONS_EDIT,
        ODS_ACTIONS_MAKE_ACTIVE,
        ODS_ACTIONS_MAKE_INACTIVE,
        ODS_DELETE,
        ODS_LOCATION_OFFICE,
        ODS_ACTIONS_ADD_SUB_LOCATION,
        ODS_ACTIONS_ADD_LOCATION,
        ODS_ROWS_PER_PAGE_LABEL,
        ODS_LOCATION_DELETE_SUCCESSFULLY,
        ODS_LOCATION_DELETE_FAILED,
        ODS_LOCATION_DELETE_CONFIRM_TITLE,
        ODS_LOCATION_DELETE_CONFIRM,
        ODS_LOCATION_REQ_LINK_ERROR,
        ODS_LOCATION_CHILD_ERROR,
        ODS_MAKE_BU_ACTIVATE_SUCCESSFUL,
        ODS_MAKE_BU_INACTIVATE_SUCCESSFUL,
        ODS_LOCATION_UPDATE_FAILED,
        ODS_UPDATE_LOCATION_MODAL_TITLE,
        ODS_ACTIVATE_LOCATION_DESCRIPTION,
        ODS_INACTIVATE_LOCATION_CONFIRMATION_DESC,
        ODS_LOCATION_SUBSIDIARIES_INACTIVATE_SUCCESSFUL,
        ODS_LOCATION_PARENT,
    } = useMultipleLocalizedText()

    const ODS_DIMENSIONS = {
		ODS_DIMENSION_PARENT_IS_INACTIVE :'ODS_DIMENSION_PARENT_IS_INACTIVE',
		ODS_DIMENSION_HAS_REQUISITIONS : 'ODS_DIMENSION_HAS_REQUISITIONS',
	}
    
    const snack = useSnackbar();
    const confirm = useConfirmation();
    const OrgElementsContext = useContext(OdsContext);
    const ODSPageSizeSessionStorage = () =>
        JSON.parse(sessionStorage.getItem('ODS_PageSize'))
            ? JSON.parse(sessionStorage.getItem('ODS_PageSize'))
            : OrgElementsContext.defaultPageSize;

    const locationFetchParams: ILocationsGetParms = useMemo(
        () => ({
            pageSize: ODSPageSizeSessionStorage(),
            pageNumber: 0,
        }),
        []
    );

    const LocationSearchParams: ILocationsSearchGetParms = useMemo(
        () => ({
            pageSize: ODSPageSizeSessionStorage(),
            pageNumber: 0,
            keywords: '',
        }),
        []
    );

    const [locFetchPayload, setLocFetchPayload] = useState<ILocationsGetParms>(locationFetchParams);
    const [locSearchPayload, setLocSearchPayload] = useState<ILocationsSearchGetParms>(LocationSearchParams);
    const [locFilterSearchPayload, setLocFilterSearchPayload] = useState<ILocationsFilterGetParms>();
    const [openAddLocDrawer, setOpenAddLocDrawer] = useState(null);
    const [listLoading, setListLoading] = useState(false);
    const [addLocation, setAddLocation] = useState(null);
    const [locHierarchyTree, setLocHierarchyTree] = useState([{ name: 'All', eid: '' }]);
    const [locDrawerData, setLocDrawerData] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [noSearchResult, setNoSearchResult] = useState(false);
    const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
    const [results, setResults] = useState<ILocationsResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    const [editLocation, setEditLocation] = useState(null);
    const [duplicateLocation, setDuplicateLocation] = useState(null);
    const [showInProgress, setShowInProgress] = useState(false);
    const [isAddSubLocation, setIsAddSubLocation] = useState(null);
    const [appliedFilterForPagination, setAppliedFilterForPagination] = useState(null);
    const [pagingContext, setPagingContext] = useState({
        hideFirstLastActions: true,
        perPage: perPageSize,
        page: 1,
        first: 1,
        last: perPageSize,
    });
    const [filtersApplied, setFiltersApplied] = useState(false);
    const searchFilters = [statusFilter, createdByFitler];

    const applyPagination = (response: any) => {
        setPagingContext({
            hideFirstLastActions: true,
            perPage: response.data.pageSize,
            page: response.data.pageNumber + 1,
            first:
                response.data.locations.length > 0
                    ? response.data.pageNumber * response.data.pageSize + 1
                    : 0,
            last:
                response.data.pageSize * response.data.pageNumber +
                response.data.locations.length,
        });
        setListLoading(false);
        response.data.totalCount == 0 ? setNoSearchResult(true) : setNoSearchResult(false);
    }

    const fetchLocations = async (locFetchPayload: ILocationsGetParms) => {
        try {
            clearAppliedFilters(null);
            setListLoading(true);
            await axios
                .get(window.jvUrls.admin + '/action/ods/locations', {
                    params: locFetchPayload,
                })
                .then(function (response) {
                    setResults(transformLocationsResult(response.data));
                    applyPagination(response);
                })
                .catch(function (error) {
                    setNoSearchResult(true);
                    console.log(error);
                    setListLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    };

    const addLocationDrawer = (e: any, node: ILocationsData) => {
        setAddLocation(true);
        setOpenAddLocDrawer(true);
    }

    useEffect(() => {
        fetchLocations(locFetchPayload);
    }, [locFetchPayload]);

    useEffect(() => {
        openAddLocDrawer!=null && fetchLocations(locFetchPayload);
        if (!openAddLocDrawer) {
            setLocDrawerData(null);
            setAddLocation(null);
            setEditLocation(null);
            setDuplicateLocation(null);
            setIsAddSubLocation(null);
        } else {
            setSearchText('')
        }
    }, [openAddLocDrawer]);

    const setLocListOnActions = () => {
        if (searchText !== '') {
            setLocHierarchyTree([{ name: 'All', eid: '' }]);
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        }
    }

    const updatePayload = (location: IBreadcrumbHierarchyObject) => {
        if (location.eid === '') {
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        } else {
            setLocFetchPayload({ ...locFetchPayload, parentEId: location.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        }
    }

    const onHandleLocClick = (location: IBreadcrumbHierarchyObject) => {
        const newEntry = { name: location.name, eid: location.eid };
        clearLocalStorageAndState();
        clearAppliedFilters(null);
        !isAvailableInBreadcrumb(locHierarchyTree, location) && setLocHierarchyTree((locHierarchyTree) => [...locHierarchyTree, newEntry]);
        setLocFetchPayload({ ...locFetchPayload, parentEId: location.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    const onHandleSearchedLocClick = (location: IBreadcrumbHierarchyObject, LocTree: Array<IBreadcrumbHierarchyObject>) => {
        let newEntry;
        if (LocTree) {
            let index = LocTree.findIndex(loc => loc.eid === location.eid);
            let LocBreadcrumb = LocTree.slice(0, index + 1);
            newEntry = [{ name: 'All', eid: '' }, ...LocBreadcrumb];
        } else {
            newEntry = [{ name: 'All', eid: '' }, { name: location.name, eid: location.eid }];
        }
        clearLocalStorageAndState();
        clearAppliedFilters(null);
        setLocHierarchyTree(newEntry);
        setLocFetchPayload({ ...locFetchPayload, parentEId: location.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    }

    const populateTitle = (node: any) => {
        if (node.subLocationsCount > 0) {
            return <React.Fragment><Tooltip title={node.name} arrow placement='bottom'><Link underline='hover' className='jv-location-title jv-location-title-link' sx={{ fontWeight: '600', maxWidth: '100%' }} onClick={() => (searchText.length > 1) ? onHandleSearchedLocClick({ name: node.name, eid: node.eid }, node.ancestors && [...node.ancestors, { name: node.name, eid: node.eid }]) : onHandleLocClick(node)} variant='body2'>{node.name}</Link></Tooltip></React.Fragment>;
        } else {
            return <React.Fragment><Tooltip title={node.name} arrow placement='bottom'><span><Typography variant='body2' className={`jv-location-title ${node.office ? "jv-office-title-chip" : "jv-office-title-no-chip"}`} sx={{maxWidth: '100%'}} disabled={!node.active}>{node.name}</Typography></span></Tooltip>{node.office && (<Chip className='jv-location-chip' label={ODS_LOCATION_OFFICE} sx={{ ml: 1, height: '26px', verticalAlign: 'text-bottom' }} />)}</React.Fragment>;
        }
    }

    const setAppliedFilterParams = (FilterData: any, pageNumber: number) => {
        setAppliedFilterForPagination(FilterData);
        if(FilterData.status && FilterData.createdByEIds){
            setLocFilterSearchPayload({ status: FilterData.status, createdByEIds: FilterData.createdByEIds.toString(), pageSize: ODSPageSizeSessionStorage(), pageNumber: pageNumber})
        } else {
            FilterData.status && setLocFilterSearchPayload({ status: FilterData.status, pageSize: ODSPageSizeSessionStorage(), pageNumber: pageNumber})
            FilterData.createdByEIds && setLocFilterSearchPayload({ createdByEIds: FilterData.createdByEIds.toString(), pageSize: ODSPageSizeSessionStorage(), pageNumber: pageNumber})
        }
        setFiltersApplied(true);
        setListLoading(true);
        setLocHierarchyTree([{ name: 'All', eid: '' }]);
    }

    const onPageChanged = (event: any, currPage: number) => {
        if (searchText.length > 1) {
            setLocSearchPayload({ ...locSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
        } else if(appliedFilterForPagination){
            setAppliedFilterParams(appliedFilterForPagination, currPage - 1 );
        } else {
            if (locHierarchyTree[locHierarchyTree.length - 1].eid === '') {
                setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
            } else {
                setLocFetchPayload({ ...locFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: locHierarchyTree[locHierarchyTree.length - 1].eid, pageNumber: currPage - 1 });
            }
        }
    }

    const onPaginationChange = (event: SelectChangeEvent) => {
        sessionStorage.setItem('ODS_PageSize', JSON.stringify(Number(event.target.value)));
        setPerPageSize(Number(event.target.value));
        if (searchText !== '') {
            setLocSearchPayload({ ...locSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: searchText })
            setListLoading(true);
            setFiltersApplied(false);
        } else if(appliedFilterForPagination){
            setAppliedFilterParams(appliedFilterForPagination, 0);
        } else {
            updatePayload(locHierarchyTree[locHierarchyTree.length - 1]);
        }
    }

    const buildSuccessSnackbarResponse = (node: ILocationsData, status: boolean): String => {
        let response = '';
        if (status) {
            response = ODS_MAKE_BU_ACTIVATE_SUCCESSFUL;
        } else {
            if (node.subLocationsCount > 0) {
                response = ODS_LOCATION_SUBSIDIARIES_INACTIVATE_SUCCESSFUL;
            } else {
                response = ODS_MAKE_BU_INACTIVATE_SUCCESSFUL;
            }
        }
        return response;
    }

    const toggleLocStatus = (node: ILocationsData, status: boolean) => {
        try {
            axios.put(
                `${window.jvUrls.admin}/action/ods/locations/activation/${node.eid}`,
                { active: status },
                { headers: { 'Content-Type': 'application/json' } })
                .then(response => {
                    setOpenAddLocDrawer(false);
                    const snackbarResponse = buildSuccessSnackbarResponse(node, status);
                    snack.showSuccess(`${node.name} ${snackbarResponse}`, OrgElementsContext.snackBarAutoHideTime);
                    searchText === '' && updatePayload(locHierarchyTree[locHierarchyTree.length - 1]);
                    searchText !== '' && setSearchText('');
                    setShowInProgress(false);
                    fetchLocations(locFetchPayload);
                }).catch(error => {
                    setShowInProgress(false);
                    if (error.response.status === 400) {
                        const { errorCode, message } = error.response.data;
                        if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_HAS_REQUISITIONS) {
                            snack.showError(message, OrgElementsContext.snackBarAutoHideTime);
                        }
                        if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_PARENT_IS_INACTIVE) {
                            confirm.open({
                                dialogLabelSet: DialogLabelSet.ALERT,
                                title: ODS_UPDATE_LOCATION_MODAL_TITLE,
                                description: message,
                                onResolve: (reason: string) => { },
                                onReject: (reason: string) => { }
                            });
                        }
                    } else {
                        snack.showError(`${ODS_LOCATION_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
                    }
                    searchText !== '' && setSearchText('');
                })
        } catch (error) {
            snack.showError(`${ODS_LOCATION_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
        }
    }

    const makeLocationActive = (node: ILocationsData) => {
        setOpenAddLocDrawer(false);
        if (node.subLocationsCount > 0) {
            confirm.open({
                dialogLabelSet: DialogLabelSet.CONFIRM,
                title: ODS_UPDATE_LOCATION_MODAL_TITLE,
                description: ODS_ACTIVATE_LOCATION_DESCRIPTION,
                onResolve: (reason: string) => {
                    setShowInProgress(true);
                    toggleLocStatus(node, true);
                    searchText !== '' && setSearchText('');
                },
                onReject: (reason: string) => {
                    setSearchText('');
                }
            });
        } else {
            setShowInProgress(true);
            toggleLocStatus(node, true);
        }
    }

    const makeLocationInActive = (node: ILocationsData) => {
        setOpenAddLocDrawer(false);
        if (node.subLocationsCount > 0) {
            confirm.open({
                dialogLabelSet: DialogLabelSet.CONFIRM,
                title: ODS_UPDATE_LOCATION_MODAL_TITLE,
                description: ODS_INACTIVATE_LOCATION_CONFIRMATION_DESC,
                onResolve: (reason: string) => {
                    setShowInProgress(true);
                    toggleLocStatus(node, false);
                    searchText !== '' && setSearchText('');
                },
                onReject: (reason: string) => {
                    setSearchText('');
                }
            });
        } else {
            setShowInProgress(true);
            toggleLocStatus(node, false);
        }
    }

    const handleDeleteResponse = (
        response: AxiosResponse<any>,
        selectedParentEId: string,
        filtersApplied: boolean
    ) => {
        setShowInProgress(false);
        openAddLocDrawer && setOpenAddLocDrawer(false);

        // Handle response
        if ([201, 200].includes(response.status)) {
            snack.showSuccess(ODS_LOCATION_DELETE_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
        } else {
            if (response.data.errorMessage.search('has children Locations') != -1) {
                snack.showError(ODS_LOCATION_CHILD_ERROR, OrgElementsContext.snackBarAutoHideTime);
            } else if (response.data.errorMessage.search('requisitions associated with it') != -1) {
                snack.showError(ODS_LOCATION_REQ_LINK_ERROR, OrgElementsContext.snackBarAutoHideTime);
            } else {
                snack.showError(ODS_LOCATION_DELETE_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
        }

        //clearing applied filters
        if (filtersApplied) {
            clearAppliedFilters(null);
        }

        //updating payload
        if (selectedParentEId !== '') {
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), parentEId: selectedParentEId, pageNumber: 0 });
        } else {
            setLocFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
        }
    };

    const deleteLocation = (node: ILocationsData) => {
        searchText !== '' && setSearchText('');
        let selectedParentEId = locHierarchyTree[locHierarchyTree.length - 1].eid;
        confirm.open({
            dialogLabelSet: DialogLabelSet.CONFIRM,
            title: ODS_LOCATION_DELETE_CONFIRM_TITLE,
            description: ODS_LOCATION_DELETE_CONFIRM,
            onResolve: (reason: string) => {
                setShowInProgress(true);
                axios
                    .delete(window.jvUrls.admin + '/action/ods/locations/' + node.eid)
                    .then(function (response) {
                        handleDeleteResponse(response, selectedParentEId, filtersApplied);
                    })
                    .catch(function (error) {
                        setShowInProgress(false);
                        openAddLocDrawer && setOpenAddLocDrawer(false);
                        if (filtersApplied) {
                            setFiltersApplied(() => !filtersApplied);
                        }
                        handleDeleteResponse(error.response, selectedParentEId, filtersApplied);
                    });
            },
            onReject: (reason: string) => {
                setSearchText('');
            },
        });
    };      

    const editSelectedLocation = (e: any, node: ILocationsData) => {
		try {
			setListLoading(true);
			setEditLocation(true);
			axios
				.get(window.jvUrls.admin + '/action/ods/locations/' + node.eid)
				.then(function (response) {
					setLocDrawerData({...response.data, subLocationsCount: node.subLocationsCount});
					setOpenAddLocDrawer(true);
				})
				.catch(function (error) {
					console.log(error);
				});
		} catch (error) {
			console.log(error);
		}
	};

    const duplicateLocationAction = (e: any, node: ILocationsData) => {
		editSelectedLocation(e, node);
		setDuplicateLocation(true);
	};

	const addSubLocationAction = (e: any, node: ILocationsData) => {
		setAddLocation(true);
        setIsAddSubLocation(true);
        setOpenAddLocDrawer(true);
	}

    const onBreadcrumChange = (locations: IBreadcrumbHierarchyObject) => {
        let breadcrumb = updateBreadcrumb(locHierarchyTree, locations)
        setLocHierarchyTree(breadcrumb);
        updatePayload(locations);
    }

    const transformActionList = (actionList: any) => {
        const locActions = [
            { i18Label: ODS_ACTIONS_ADD_SUB_LOCATION, name: 'addSubLocation' },
            { i18Label: ODS_ACTIONS_DUPLICATE, name: 'duplicate' },
            { i18Label: ODS_ACTIONS_EDIT, name: 'edit' },
            { i18Label: ODS_ACTIONS_MAKE_ACTIVE, name: 'makeActive' },
            { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: 'makeInactive' },
            { i18Label: ODS_DELETE, name: 'delete' },
        ];
        const actionLinks: any = [];
        if (actionList) {
            Object.keys(actionList).forEach((key) => {
                let filteredAction = locActions.filter((e) => e.name === key);
                if (filteredAction.length > 0 && actionList[key] !== 'hidden' && actionList[key] !== null) {
                    let actionTertiaryLink = {
                        type: 'tertiary',
                        label: filteredAction[0].i18Label,
                        disabled: actionList[key] === 'grayout',
                        onClick: (event: any, data: any) => {
                            setLocDrawerData(data);
                            setLocListOnActions();
                            localStorage.removeItem('locationSearchFilter');
                            filteredAction[0].name === 'duplicate' && duplicateLocationAction(event, data);
                            filteredAction[0].name === 'addSubLocation' && addSubLocationAction(event, data);
                            filteredAction[0].name === 'edit' && editSelectedLocation(event, data);
                            filteredAction[0].name === 'makeActive' && makeLocationActive(data);
                            filteredAction[0].name === 'makeInactive' && makeLocationInActive(data);
                            filteredAction[0].name === 'delete' && deleteLocation(data);
                        }
                    };
                    actionLinks.push(actionTertiaryLink);
                }
            });
        }
        return actionLinks;
    };

    const renderParentLoc = (node: ILocationsData) => {
		const list: any = [];
		if (node.ancestors && node.ancestors.length > 0) {
			node.ancestors.map((parent, index) => {
				list.push(
					<span style={{ display: 'inline-flex' }}>
                        <React.Fragment>
						    <Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" className='jv-location-title jv-location-title-link' sx={{ maxWidth: '120px', fontWeight: '600' }} onClick={() => onHandleSearchedLocClick({ name: parent.name, eid: parent.eid }, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
						    <span style={{ padding: '0 6px' }}>{index < node.ancestors.length - 1 && ' /   '}</span>
                        </React.Fragment>
					</span>
				);
			})
		}
		return list;
	}

    useEffect(() => {
        localStorage.removeItem('locationSearchFilter');
    }, []);

    const fetchSearchFilterLocation = (param : any) => {
        axios.get(window.jvUrls.admin + '/action/ods/locations/search', {
            params: param
        })
        .then(function (response) {
            setResults(transformLocationsResult(response.data));
            applyPagination(response);
        })
        .catch(function (error) {
            console.log(error);
            setListLoading(false);
        });
    }

    useEffect(() => {
        if (searchText && searchText.length > 1) {
			fetchSearchFilterLocation(locSearchPayload);
		}
	}, [searchText, locSearchPayload]);

    useEffect(() => {
        if (locFilterSearchPayload) {
            fetchSearchFilterLocation(locFilterSearchPayload);
        }
    }, [locFilterSearchPayload]);


    const handleSearch = (props: ListSearchChangeProps) => {
        if (props.appliedSearchFilters.length === 0 && props.q === '') {
            updatePayload(locHierarchyTree[0]);
            clearLocalStorageAndState();
            setLocHierarchyTree([{ name: 'All', eid: '' }]);
        } else if (props.q !== '' && searchText === '') {
            filtersApplied && clearAppliedFilters(null);
            handleKeywordSearch(props);
        } else if (props.appliedSearchFilters.length > 0) {
            searchText && clearLocalStorageAndState();
            handleAppliedFilterSearch(props);
        } else {
            handleNoSearchResultState(props);
        }
    };

    const clearLocalStorageAndState = () => {
        localStorage.removeItem('locationSearchFilter');
        setSearchText('');
    };

    const handleKeywordSearch = (props: ListSearchChangeProps) => {
        setSearchText(props.q);
        setLocSearchPayload({
            ...locSearchPayload,
            pageSize: ODSPageSizeSessionStorage(),
            pageNumber: 0,
            keywords: encodeURIComponent(props.q)
        });
        setListLoading(true);
        localStorage.setItem('locationSearchFilter', JSON.stringify({ q: props.q }));
    };

    const handleAppliedFilterSearch = (props: ListSearchChangeProps) => {
        const filterSearchPayload = transformLocationFilterData(props.appliedSearchFilters);
        setAppliedFilterParams(filterSearchPayload, 0);
        localStorage.setItem('locationSearchFilter', JSON.stringify({ filterData: filterSearchPayload }));
        
    };

    const clearAppliedFilters = (searchFilters: ListSearchChangeProps | null) => {
        if (searchFilters !== null) {
            searchFilters.appliedSearchFilters = [];
            localStorage.setItem('locationSearchFilter', JSON.stringify({ q: searchFilters.q }));
        }
    
        setAppliedFilter([]);
        setFiltersApplied(false);
        setAppliedFilterForPagination(null);
    };

    const handleNoSearchResultState = (props: ListSearchChangeProps) => {
        setNoSearchResult(true);
        handleKeywordSearch(props);
    };

    const [appliedFilter, setAppliedFilter] = useState<ListAppliedSearchFilterProps>();
    const theme = createInternalTheme({ darkMode: false, theme: {} });

    const LocationsBreadcrumb = () => <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {(filtersApplied &&
            <Breadcrumb variant='primaryLink' className='ods-breadcrumb location-breadcrumb'>
                <BreadcrumbItem variant label={ODS_ALL} className='location-breadcrumb-all' onClick={() => { onBreadcrumChange({ name: ODS_ALL, eid: '' }); clearAppliedFilters(null) }} />
                <BreadcrumbItem variant maxLength={60} className='location-breadcrumb-search-all' label={'Filter Results'} />
            </Breadcrumb>
        )}
        {((!filtersApplied && searchText.length === 0) &&
            <Breadcrumb variant='primaryLink' className='ods-breadcrumb location-breadcrumb' maxItems={5} itemsBeforeCollapse={2} itemsAfterCollapse={2}>
                {locHierarchyTree.map((locations, i) => (i === locHierarchyTree.length - 1) ? (
                    <BreadcrumbItem key={i} variant label={locations.name} className={'location-breadcrumb-link' + i} />
                ) : (<BreadcrumbItem key={i} className={'location-breadcrumb-link' + i} onClick={() => onBreadcrumChange(locations)} variant label={locations.name} />))}
            </Breadcrumb>
        )}
        {(searchText.length > 1 && <Breadcrumb variant='primaryLink' className='ods-breadcrumb location-breadcrumb'>
            <BreadcrumbItem variant label={ODS_ALL} className='location-breadcrumb-all' onClick={() => { onBreadcrumChange({ name: ODS_ALL, eid: '' }); setSearchText('') }} />
            <BreadcrumbItem variant maxLength={60} className='location-breadcrumb-search-all' label={'Search Results for: "' + searchText + '"'} />
        </Breadcrumb>)}
    </Paper>;

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <LayoutRoot>
                    <LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
                        <Button key='contained' variant='contained' className='ods-header-button location-add-button' onClick={addLocationDrawer} color='primary'>{ODS_ACTIONS_ADD_LOCATION}</Button>
                        <PageHeader title={ODS_TAB_LOCATIONS} className={'location-header-text'} variant="subHeader" />
                        <LocationsBreadcrumb />
                        {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
                        <ListBase
                            showSearch
                            className='location-list-base'
                            labelSearch={ODS_SEARCH_PLACEHOLDER}
                            labelSearchAria={ODS_SEARCH_LOCATIONS}
                            // @ts-ignore
                            onSearch={handleSearch}
                            searchFilters={searchFilters}
                            labelClose = "Cancel"
                            appliedSearchFilters={appliedFilter}
                            results={results}
                            appliedSearchQuery={searchText}
                            pagingContext={pagingContext}
                            onPageChanged={onPageChanged}
                            onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                            rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
                            resultsPerPage={perPageSize}
                            perPageOptions={OrgElementsContext.perPageSelectOptions}
                            viewContexts={[
                                {
                                    ariaLabel: 'Datatable View',
                                    icon: <TableChartIcon />,
                                    renderResults: (viewContextProps) => {
                                        if (listLoading) {
                                            return (<div className='location-list-loader' style={{ display: 'flex', justifyContent: 'center' }}>
                                                <CircularProgress size={34} />
                                            </div>)
                                        } else {
                                            if (!noSearchResult) {
                                                return (<DataTable className='location-data-table'
                                                    {...viewContextProps}
                                                    getRowID={node => node.id}
                                                    columns={
                                                        [
                                                            { id: 'Location', columnName: ODS_TAB_LOCATIONS, maxWidth: 175, renderCell: (node: ILocationsData) => populateTitle(node) },
                                                            { id: 'SubLocations', columnName: ODS_SUB_LOCATIONS, renderCell: (node: ILocationsData) => <Typography variant='body2' disabled={!node.active}>{node.subLocationsCount}</Typography>, maxWidth: 60 },
                                                            ...(searchText.length > 1 || filtersApplied ? [{ id: 'ParentLocation', columnName: ODS_LOCATION_PARENT, maxWidth: 325, renderCell: (node: ILocationsData) => renderParentLoc(node), },] : []),
                                                            { id: 'created', columnName: ODS_DATE_CREATED, renderCell: (node: ILocationsData) => <Typography className='jv-location-desc' variant='body2' disabled={!node.active}>{Moment(new Date(node.createdDate).toLocaleString(window._jvselfUser.locale.languageTag, {timeZone: window._jvselfUser.locale.timezone})).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase())}</Typography>, maxWidth: 70 },
                                                            { id: 'createdby', columnName: ODS_CREATED_BY, renderCell: (node: ILocationsData) => <Tooltip title={node.createdByUser} arrow placement='bottom'><span><Typography className='jv-location-createdby' variant='body2' disabled={!node.active}>{node.createdByUser}</Typography></span></Tooltip>, maxWidth: 100 },
                                                            { id: 'Status', columnName: ODS_STATUS, renderCell: (node: ILocationsData) => (node.active) ? <Typography variant='body2' color='success' sx={{ color: 'green' }}>Active</Typography> : <Typography variant='body2' color='error' className='error-text-style' disabled>Inactive</Typography>, maxWidth: 45 },
                                                        ]
                                                    }
                                                    renderActions={(node: ILocationsData) => {
                                                        return (<ActionGroup id='primary-buttons-actions' actions={transformActionList(node.actions)} data={node} />)
                                                    }}
                                                />)
                                            } else {
                                                return (<div className={'location-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                                            }
                                        }
                                    },
                                }
                            ]}
                        />

                    </LayoutRootSection>
                </LayoutRoot>
                <AddLocation
                    addDrawerState={openAddLocDrawer}
                    setaddDrawerState={setOpenAddLocDrawer}
                    addLocation={addLocation}
                    isAddSubLocation={isAddSubLocation}
                    editLocation={editLocation}
                    locDrawerData={locDrawerData}
                    duplicateLocation={duplicateLocation}
                    setDuplicateLocation={setDuplicateLocation}
                    makeLocationActive={makeLocationActive}
                    makeLocationInActive={makeLocationInActive}
                    deleteLocation={deleteLocation}
                />
            </ThemeProvider>
        </React.StrictMode>
    );
}

export default Locations;
